import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Row, Col, InputGroup, Tab, Tabs, Alert } from 'react-bootstrap';
import { DatePicker, MonthPicker } from '@rd-web-markets/shared/dist/util/date';
import ChangeHighlightedImmutableUnderReviewControl from '@rd-web-markets/shared/dist/util/reviewEditControls/ChangeHighlightedImmutableUnderReviewControl';
import ImmutableUnderReviewFormControl from '@rd-web-markets/shared/dist/util/reviewEditControls/ImmutableUnderReviewFormControl';
import ImmutableUnderReviewFormCheck from '@rd-web-markets/shared/dist/util/reviewEditControls/ImmutableUnderReviewFormCheck';
import * as QueryString from 'query-string';
import { useTranslation } from 'react-i18next';
import ContactsSection from '@rd-web-markets/shared/dist/technicalProof/ContactsSection';
import { useSelector } from 'react-redux';
import { useGetUserClaimGroupAbilities } from '../userAbilities';
import ProjectActivities from './ProjectActivities';
import { ClaimGroup } from '../models/ClaimGroup';
import ProjectReportSlicesList from './projectReportSlice/ProjectReportSlicesList';
import AiProjectReportSliceList from './projectReportSlice/AiProjectReportSliceList';
import { useLocation, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { claimProjectReportsAllService } from '@rd-web-markets/shared/dist/services/all/claim_project_reports_all.service';
import SummarySection from './summarySection/SummarySection';
import { ProjectSelector } from '@rd-web-markets/shared/dist/claim_project_report/ProjectSelector';
import { AddButton } from '@rd-web-markets/shared/dist/util/buttons';
import customerContractsService from './customerContracts/customer_contract.service';
import CustomerContractForm from './customerContracts/CustomerContractForm';
import CustomerContractsTable from './customerContracts/CustomerContractTable';
const ProjectReportTab = _ref => {
  let {
    handleChange,
    toggleOngoing,
    setReport,
    handleSliceChange,
    isOngoing,
    handleSubmit,
    handleTinyMce,
    report,
    handleDateChange,
    claimGroup,
    company,
    setClaimGroup,
    reportNotesHandlers
  } = _ref;
  const {
    t
  } = useTranslation();
  const accountType = useSelector(state => state.auth.user.account_type);
  const isClient = accountType === 'customer';
  const [isUnderReview, setIsUnderReview] = useState(!!report?.active_change_set);
  const [tabKey, setTabKey] = useState(QueryString.parse(window.location.search) === '' ? 'summary' : QueryString.parse(window.location.search).query);
  const [groupingProjects, setGroupingProjects, fetchAllReports] = claimProjectReportsAllService.useFetchAll({
    claimGroupId: claimGroup.id
  });
  const projectSelectorSearchRef = useRef(null);
  const [isProjectSelectorOpen, setIsProjectSelectorOpen] = useState(false);
  useEffect(() => {
    if (isProjectSelectorOpen) {
      projectSelectorSearchRef.current?.focus();
    }
  }, [isProjectSelectorOpen]);
  const [showContractForm, setShowContractForm] = useState(false);
  const [contractsLoading, setContractsLoading] = useState(false);
  const {
    contracts,
    createContract,
    updateContract,
    deleteContract
  } = customerContractsService.useCrud(setContractsLoading, report.id);
  const handleCreateContract = async formData => {
    await createContract(formData);
    setShowContractForm(false);
  };
  {/* this is a bug fix to force the ai drafter to re-render. The issue is that the project_report_tab stays rendered all the time, so 
   if we click the ai_drafter tab - that loads the file fetching hook, then we switch from the project_report_tab to documents - that changes the url search params.
   Because the original hook from ai_drafter never unmounted - it fetches files again, in fact each time we change folders, it fetches files again. So when we come back
   to project_report_tab - because it never unmounted, the selected tab is still ai_drafter, and it now shows the latest files that the document tab showed.
   We can't fix this by simply making the project_report_tab re-render - because for some reason it renders so slowly - it makes it buggy when you change tab - to say notes.
   It goes back to project_report_tab automatically.
   */}
  const {
    search
  } = useLocation();
  const key = new URLSearchParams(search).get('key');
  const getProjectStatusOptions = () => {
    if (report.assessment === 'in_progress') {
      return [{
        value: 'unstarted',
        label: t('not_started')
      }, {
        value: 'shortlisted_sampled',
        label: t('shortlisted_sampled')
      }, {
        value: 'scoping_scheduled',
        label: t('scoping_scheduled')
      }];
    } else if (report.assessment === 'completed') {
      return [{
        value: 'scoping_completed',
        label: t('scoping_completed')
      }, {
        value: 'begun_tech_interview_scheduled',
        label: t('in_progress_tech_interview_scheduled')
      }, {
        value: 'begun_tech_interview_completed',
        label: t('in_progress_tech_interview_completed')
      }, {
        value: 'begun_in_manager_qa',
        label: t('in_progress_in_manager_qa')
      }, {
        value: 'begun_manager_qa_completed',
        label: t('in_progress_manager_qa_completed')
      }, {
        value: 'finished',
        label: t('completed')
      }];
    } else {
      return []; // For 'not_started' or blank
    }
  };
  const filteredProjectStatusOptions = getProjectStatusOptions();
  const shouldDisableProjectStatus = !report.assessment || report.assessment === 'not_started';
  const user = useSelector(state => state.auth.user);
  const userClaimGroupAbilities = useGetUserClaimGroupAbilities({
    user,
    claimGroup
  });
  const pageAccess = userClaimGroupAbilities.pages.ClaimProjectReportDetailPage;
  const showProjectNotesTab = !isClient || report.project_notes_visibility_mode !== 'not_visible';
  const showTechnicalNarrativeTab = pageAccess.canAccessTechnicalNarrativeSubtab && (!isClient || report.technical_narrative_visibility_mode !== 'not_visible');
  return /*#__PURE__*/React.createElement(React.Fragment, null, isUnderReview && /*#__PURE__*/React.createElement(Alert, {
    key: "warning",
    variant: "warning"
  }, t('project_is_under_review')), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('project_name')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: handleSubmit,
    name: "project_name",
    object: report,
    size: "md",
    type: "text",
    disabled: !pageAccess.canEditProjectDetails || isUnderReview
  })), /*#__PURE__*/React.createElement(Col, {
    md: {
      span: 2,
      offset: 2
    }
  }, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Check,
    object: report,
    onBlur: handleSubmit,
    name: "include_in_technical_report",
    onChange: e => handleChange(e),
    label: t('include_in_rnd_report'),
    disabled: !pageAccess.canEditProjectDetails || isUnderReview
  }))), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('project_dates')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(InputGroup, {
    style: {
      'flexWrap': 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(Col, {
    md: 4,
    className: "p-0"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewFormControl, {
    as: MonthPicker,
    onBlur: handleSubmit,
    selected: report.start_date,
    onChange: date => handleDateChange({
      target: {
        name: 'start_date',
        value: date
      }
    }),
    disabled: !pageAccess.canEditProjectDetails || isUnderReview
  })), !isOngoing && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Col, {
    md: 1,
    className: "p-0 ms-2 me-2"
  }, /*#__PURE__*/React.createElement(InputGroup.Text, null, t('to'))), /*#__PURE__*/React.createElement(Col, {
    md: 4,
    className: "p-0"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewFormControl, {
    as: MonthPicker,
    onBlur: handleSubmit,
    selected: report.end_date,
    onChange: date => handleDateChange({
      target: {
        name: 'end_date',
        value: date
      }
    }),
    disabled: !pageAccess.canEditProjectDetails || isUnderReview
  }))), /*#__PURE__*/React.createElement(Col, {
    md: 3,
    className: "p-1"
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewFormCheck, {
    disabled: !pageAccess.canEditProjectDetails || isUnderReview,
    checked: isOngoing,
    label: "Ongoing",
    onChange: e => toggleOngoing(e),
    onBlur: handleSubmit
  }))))), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('project_code')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    disabled: !pageAccess.canEditProjectDetails || isUnderReview,
    as: Form.Control,
    onChange: handleChange,
    name: "code",
    onBlur: handleSubmit,
    object: report,
    size: "md",
    type: "text"
  }))), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('eligibility_assessment')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewFormControl, {
    disabled: !pageAccess.canEditProjectDetails || isUnderReview,
    onBlur: handleSubmit,
    as: "select",
    name: "assessment",
    onChange: handleChange,
    size: "md",
    value: report.assessment
  }, /*#__PURE__*/React.createElement("option", {
    value: null
  }), /*#__PURE__*/React.createElement("option", {
    value: "not_started"
  }, t('not_started')), /*#__PURE__*/React.createElement("option", {
    value: "in_progress"
  }, t('in_progress')), /*#__PURE__*/React.createElement("option", {
    value: "completed"
  }, t('completed'))))), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('project_status')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewFormControl, {
    as: "select",
    disabled: !pageAccess.canEditProjectDetails || isUnderReview || shouldDisableProjectStatus,
    onBlur: handleSubmit,
    name: "completion_status",
    onChange: handleChange,
    size: "md",
    value: report.completion_status
  }, /*#__PURE__*/React.createElement("option", {
    value: null
  }), filteredProjectStatusOptions.map(opt => /*#__PURE__*/React.createElement("option", {
    key: opt.value,
    value: opt.value
  }, opt.label))))), claimGroup.rate_type === 'sme_and_rdec' && /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('rate_type')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewFormControl, {
    as: "select",
    disabled: !pageAccess.canEditProjectDetails || isUnderReview,
    onBlur: handleSubmit,
    name: "rate_type",
    onChange: handleChange,
    size: "md",
    value: report.rate_type
  }, /*#__PURE__*/React.createElement("option", {
    value: ""
  }), /*#__PURE__*/React.createElement("option", {
    value: "sme"
  }, t('sme')), /*#__PURE__*/React.createElement("option", {
    value: "rdec"
  }, t('rdec'))))), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('commecrial_project_type')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewFormControl, {
    as: "select",
    disabled: !pageAccess.canEditProjectDetails || isUnderReview,
    onBlur: handleSubmit,
    name: "program_type",
    onChange: handleChange,
    size: "md",
    value: report.program_type
  }, /*#__PURE__*/React.createElement("option", {
    value: "project"
  }, t('project')), /*#__PURE__*/React.createElement("option", {
    value: "program_commercial_project"
  }, t('program_commercial_project')), /*#__PURE__*/React.createElement("option", {
    value: "connected_to_program_commercial_included_cost"
  }, t('connected_to_program_commercial_included_cost')), /*#__PURE__*/React.createElement("option", {
    value: "connected_to_program_commercial_not_included_cost"
  }, t('connected_to_program_commercial_not_included_cost'))))), (report.program_type === 'connected_to_program_commercial_included_cost' || report.program_type === 'connected_to_program_commercial_not_included_cost') && /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('connected_to_program_commercial_project')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement("button", {
    className: "form-control d-flex align-items-center justify-content-between",
    "data-testid": "connected-to-commercial-project-dropdown-button",
    type: "button",
    disabled: !pageAccess.canEditProjectDetails || isUnderReview,
    onClick: () => {
      setIsProjectSelectorOpen(true);
    }
  }, /*#__PURE__*/React.createElement("span", null, report.commercial_program_project?.project_name || ''), /*#__PURE__*/React.createElement("span", {
    className: "material-icons ms-2"
  }, "arrow_drop_down")), isProjectSelectorOpen && /*#__PURE__*/React.createElement(ProjectSelector, {
    claimGroupId: claimGroup.id,
    alreadySelectedInstances: [{
      id: report.commercial_program_project_id
    }],
    onChange: e => {
      handleChange(e);
      setIsProjectSelectorOpen(false);
    },
    onBlur: e => handleSubmit(e, true),
    disabled: !pageAccess.canEditProjectDetails || isUnderReview,
    eventFieldName: 'commercial_program_project_id',
    searchInputRef: projectSelectorSearchRef,
    queryParams: {
      query: {
        program_type: 'program_commercial_project'
      }
    }
  }))), report.completion_status === 'begun_tech_interview_completed' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('tech_interview_completed_at')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(ImmutableUnderReviewFormControl, {
    as: DatePicker,
    disabled: !pageAccess.canEditProjectDetails || isUnderReview,
    selected: report.technical_interview_completed_at,
    onChange: date => handleDateChange({
      target: {
        name: 'technical_interview_completed_at',
        value: date
      }
    })
  }))), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('technical_interview_completed_by')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    disabled: !pageAccess.canEditProjectDetails || isUnderReview,
    onChange: handleChange,
    onBlur: handleSubmit,
    name: "technical_interview_completed_by",
    object: report,
    size: "md",
    type: "text"
  })))), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('created_by')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    disabled: true,
    as: Form.Control,
    onChange: handleChange,
    name: "created_by",
    onBlur: handleSubmit,
    object: report,
    size: "md",
    type: "text"
  }))), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-2",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: "md",
    md: 4
  }, t('creation_date')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    disabled: true,
    as: Form.Control,
    onChange: handleChange,
    name: "created_at_formatted",
    onBlur: handleSubmit,
    object: report,
    size: "md",
    type: "text"
  }))), /*#__PURE__*/React.createElement("hr", {
    className: "my-4"
  }), /*#__PURE__*/React.createElement(Row, {
    className: "mb-3"
  }, /*#__PURE__*/React.createElement(Col, {
    md: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-between align-items-center mb-2"
  }, /*#__PURE__*/React.createElement("h5", {
    className: "text-primary mb-0"
  }, t('customer_contracts')), /*#__PURE__*/React.createElement(AddButton, {
    size: "sm",
    variant: "info",
    disabled: contractsLoading || !pageAccess.canEditProjectDetails || isUnderReview,
    onClick: () => setShowContractForm(prev => !prev),
    text: showContractForm ? t('hide_form') : t('add_contract')
  })), /*#__PURE__*/React.createElement(Form.Group, {
    className: "mb-3",
    as: Row
  }, /*#__PURE__*/React.createElement(Form.Label, {
    column: true,
    md: 4
  }, t('customer_name')), /*#__PURE__*/React.createElement(Col, {
    md: 4
  }, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: handleSubmit,
    name: "customer_name",
    object: report,
    size: "md",
    type: "text",
    disabled: !pageAccess.canEditProjectDetails || isUnderReview,
    placeholder: t('enter_customer_name')
  }))), showContractForm && /*#__PURE__*/React.createElement(CustomerContractForm, {
    onSubmit: handleCreateContract,
    loading: contractsLoading
  }), contracts.length === 0 && !showContractForm ? /*#__PURE__*/React.createElement("div", {
    className: "text-center py-4"
  }, /*#__PURE__*/React.createElement("p", {
    className: "text-muted mb-0"
  }, t('no_contracts_yet'))) : contracts.length > 0 ? /*#__PURE__*/React.createElement(CustomerContractsTable, {
    contracts: contracts,
    onUpdate: updateContract,
    onDelete: deleteContract,
    userCanEdit: pageAccess.canEditProjectDetails && !isUnderReview,
    loading: contractsLoading
  }) : null)), /*#__PURE__*/React.createElement("hr", {
    className: "my-4"
  }), /*#__PURE__*/React.createElement(Tabs, {
    onSelect: k => setTabKey(k),
    activeKey: tabKey,
    id: "uncontrolled-tab-example",
    className: "mb-3"
  }, pageAccess.canAccessSummarySubtab && /*#__PURE__*/React.createElement(Tab, {
    eventKey: "summary",
    title: t('summary')
  }, /*#__PURE__*/React.createElement(SummarySection, {
    handleSubmit: handleSubmit,
    isUnderReview: isUnderReview,
    toggleOngoing: toggleOngoing,
    isOngoing: isOngoing,
    company: company,
    report: report,
    setReport: setReport,
    handleChange: handleChange,
    claimGroup: claimGroup,
    handleDateChange: handleDateChange,
    pageAccess: pageAccess,
    reportNotesHandlers: reportNotesHandlers
  })), showProjectNotesTab && /*#__PURE__*/React.createElement(Tab, {
    eventKey: "project_activities",
    title: t('project_notes')
  }, /*#__PURE__*/React.createElement(ProjectActivities, {
    pageAccess: pageAccess,
    setReport: setReport,
    handleSubmit: handleSubmit,
    handleChange: handleChange,
    report: report,
    handleTinyMce: handleTinyMce,
    handleSliceChange: handleSliceChange,
    reportNotesHandlers: reportNotesHandlers,
    isClient: isClient,
    isDisabled: isUnderReview
  })), pageAccess.canAccessAiDrafterSubTab && /*#__PURE__*/React.createElement(Tab, {
    eventKey: "ai_drafter",
    title: t('ai_drafter')
  }, tabKey === 'ai_drafter' && key === 'project_report_tab' && /*#__PURE__*/React.createElement(AiProjectReportSliceList, {
    setReport: setReport,
    handleSubmit: handleSubmit,
    report: report,
    handleTinyMce: handleTinyMce,
    handleSliceChange: handleSliceChange,
    pageAccess: pageAccess
  })), showTechnicalNarrativeTab && /*#__PURE__*/React.createElement(Tab, {
    eventKey: "technical_narrative",
    title: t('technical_narrative')
  }, /*#__PURE__*/React.createElement(ProjectReportSlicesList, {
    setReport: setReport,
    handleSubmit: handleSubmit,
    handleChange: handleChange,
    report: report,
    handleTinyMce: handleTinyMce,
    handleSliceChange: handleSliceChange,
    pageAccess: pageAccess,
    isClient: isClient,
    isDisabled: isUnderReview
  }))));
};
export default ProjectReportTab;